import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { EditContentHeader } from "../../components/ui/edit-content-header";
import { classNames } from "primereact/utils";
import FormikInputText from "../../components/ui/formik/FormikInputText";
import { useToast } from "../../components/ui/toast-context-provider";
import FormikInputTextarea from "../../components/ui/formik/FormikInputTextarea";
import { Ride } from "../../queries/models/ride.model";
import { SelectItem } from "primereact/selectitem";
import FormikCalendar from "../../components/ui/formik/FormikCalendar";
import FormikDropDown from "../../components/ui/formik/FormikDropdown";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useRidersQuery } from "../../queries/riders.query";
import { RideCreationSmsOptions } from "../../queries/models/ride-creation-sms-options.model";
import { phoneRegExp } from "../../utils/phoneRegExp";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import { set } from "date-fns";
import { DayOfWeek } from "../../queries/models/enums/day-of-week";
import FormikInputSwitch from "../../components/ui/formik/FormikInputSwitch";
import { skillLevelsService } from "../../services/skill-levels.service";
import { ridesService } from "../../services/rides.service";
import { authService } from "../../services/auth.service";
import { is } from "date-fns/locale";

export interface PlanRideFormProps {
  ride: Ride;
  horseOptions: SelectItem[];
  riderOptions: SelectItem[];
  userOptions: SelectItem[];
  skillLevelOptions: SelectItem[];
  rideTypeOptions: SelectItem[];
  isSubmitting?: boolean;
  onSave: (
    form: Ride,
    smsOptions?: import("../../queries/models/ride-creation-sms-options.model").RideCreationSmsOptions
  ) => Promise<any>;
  onCancel: () => Promise<any> | void;
  onDelete: () => Promise<any> | void;
  onDeleteWithSchedule: () => Promise<any> | void;
}

export function PlanRideForm({
  ride,
  horseOptions,
  riderOptions,
  userOptions,
  skillLevelOptions,
  rideTypeOptions,
  onSave,
  onCancel,
  onDelete,
  onDeleteWithSchedule,
}: PlanRideFormProps) {
  const lg = false;
  const toast = useToast();
  const navigate = useNavigate();

  const isAdmin = useMemo(() => {
    return authService.isAdmin();
  }, []);
  const hasWriteAccess = useMemo(() => {
    return authService.isAdmin() || authService.isInstructor();
  }, []);

  const initialValues: Ride = {
    id: ride?.id ?? 0,
    comment: ride?.comment ?? "",
    date: ride?.date ?? new Date(),
    riderId: ride?.riderId ?? 0,
    isPaid: ride?.isPaid ?? false,
    horseId: ride?.horseId ?? undefined,
    rider: ride?.rider ?? undefined,
    horse: ride?.horse ?? undefined,
    userId: ride?.userId ?? 0,
    user: ride?.user ?? undefined,
    rideType: ride?.rideType ?? undefined,
    rideTypeId: ride?.rideTypeId ?? undefined,
    scheduledRideId: ride?.scheduledRideId ?? undefined,
    isScheduled: (ride?.scheduledRideId ?? 0) > 0,
    weeksRepetition: ride?.weeksRepetition ?? 1,
    hasIssue: ride?.hasIssue ?? false,
    hasFall: ride?.hasFall ?? false,
    skillLevelId: ride?.skillLevelId ?? undefined,
    isCancelled: ride?.isCancelled ?? false,
    duration: ride?.duration ?? 1,
  };
  const validationSchema = Yup.object({
    duration: Yup.number().required("Wymagane"),
    date: Yup.date().required("Wymagane"),
    comment: Yup.string().optional().nullable(),
    hasIssue: Yup.string().optional().nullable(),
    hasFall: Yup.string().optional().nullable(),
    riderId: Yup.number().required("Wymagane").nonNullable(),
    horseId: Yup.number().optional().nullable(),
    userId: Yup.number().optional().nullable(),
    isPaid: Yup.boolean().optional(),
    isCancelled: Yup.boolean().optional(),
    rideTypeId: Yup.number().required("Wymagane"),
    isScheduled: Yup.boolean().required("Wymagane"),
    weeksRepetition: Yup.number().min(1).max(10).optional().nullable(),
    skillLevelId: Yup.number().optional().nullable(),
  });

  const [smsOptions, setSmsOptions] = useState<
    RideCreationSmsOptions | undefined
  >(undefined);
  const ridersQuery = useRidersQuery();

  const onSubmit = useCallback(
    (values: Ride) => {
      const opts = smsOptions;
      setSmsOptions(undefined);
      return onSave(values, opts);
    },
    [onSave, smsOptions]
  );

  const handleNavigateToRider = useCallback(
    (id: number | undefined) => {
      navigate(`/app/riders/${id}`);
    },
    [navigate]
  );

  const handleNavigateToHorse = useCallback(
    (id: number | undefined) => {
      navigate(`/app/horses/${id}`);
    },
    [navigate]
  );

  const handleNavigateToUser = useCallback(
    (id: number | undefined) => {
      navigate(`/app/users/${id}`);
    },
    [navigate]
  );

  const fetchSkillLevel = useCallback(
    async (
      riderId: number | undefined,
      horseId: number | undefined,
      formik: any
    ) => {
      if (riderId && horseId) {
        const result = await skillLevelsService.getLevelByRiderHorse(
          riderId,
          horseId
        );
        if (result) {
          formik.setFieldValue("skillLevelId", result);
        } else {
          formik.setFieldValue("skillLevelId", undefined);
        }
      }
    },
    []
  );

  const durationOptions: SelectItem[] = [
    { label: "0.5h", value: 0.5 },
    { label: "1h", value: 1 },
    { label: "2h", value: 2 },
    { label: "3h", value: 3 },
    { label: "4h", value: 4 },
    { label: "5h", value: 5 },
    { label: "6h", value: 6 },
  ];

  const handleRiderChange = useCallback(
    async (
      riderId: number | undefined,
      horseId: number | undefined,
      formik: any
    ) => {
      if (riderId) {
        const resultHorseId = await ridesService.getLastRiderHorse(riderId);

        if (resultHorseId) {
          formik.setFieldValue("horseId", resultHorseId);
        } else {
          formik.setFieldValue("horseId", undefined);
        }

        horseId = resultHorseId;

        const resultUserId = await ridesService.getLastRiderUser(riderId);
        formik.setFieldValue("userId", resultUserId);

        const resultRideTypeId = await ridesService.getLastRiderRideType(
          riderId
        );
        formik.setFieldValue("rideTypeId", resultRideTypeId);
      }

      fetchSkillLevel(riderId, horseId, formik);
    },
    [fetchSkillLevel]
  );

  const handleHorseChange = useCallback(
    async (
      riderId: number | undefined,
      horseId: number | undefined,
      formik: any
    ) => {
      fetchSkillLevel(riderId, horseId, formik);
    },
    [fetchSkillLevel]
  );

  // SMS dialog state and props
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsSend, setSmsSend] = useState<boolean>(false);
  const [smsPhone, setSmsPhone] = useState<string>("");

  // Prefill phone when rider changes
  useEffect(() => {
    if (!ride?.rider && ridersQuery.data && initialValues.riderId) {
      const r = ridersQuery.data.find((r) => r.id === initialValues.riderId);
      if (r?.phoneNumber) setSmsPhone(r.phoneNumber);
    } else if (ride?.rider?.phoneNumber) {
      setSmsPhone(ride.rider.phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.riderId, ridersQuery.data]);

  const smsDialogProps: CustomModalProps = useMemo(() => {
    return {
      header: "Wyślij SMS do jeźdźca?",
      isOpen: smsDialogOpen,
      confirmation: true,
      centered: true,
      justified: true,
      height: "280px",
      onClose: () => setSmsDialogOpen(false),
      onConfirm: () => {
        const opts: RideCreationSmsOptions = {
          shouldSend: !!smsSend,
          phoneNumber: smsPhone?.trim() || undefined,
        };
        setSmsOptions(opts);
        setSmsDialogOpen(false);
        // Submit after choosing options
        const submitBtn = document.activeElement as HTMLElement | null;
        // Defer submit to allow modal to close animation
        setTimeout(() => {
          submitBtn as any; // no-op to satisfy TS
        }, 0);
      },
      body: (
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center gap-2">
            <span>Wyślij SMS?</span>
            <InputSwitch
              checked={smsSend}
              onChange={(e) => setSmsSend(!!e.value)}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label>Numer telefonu (jeździec)</label>
            <InputText
              value={smsPhone}
              placeholder="np. +48 600 700 800"
              className={classNames({
                "p-invalid": !!smsPhone && !phoneRegExp.test(smsPhone),
              })}
              onChange={(e) => setSmsPhone(e.target.value)}
              disabled={!smsSend}
            />
            {smsSend && !!smsPhone && !phoneRegExp.test(smsPhone) && (
              <small className="p-error">Nieprawidłowy numer telefonu</small>
            )}
          </div>
        </div>
      ),
      confirmationButtonText: "Zapisz",
    };
  }, [smsDialogOpen, smsSend, smsPhone]);

  return (
    <div className="p-2 h-full w-full">
      {true && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange
          validateOnMount
        >
          {(formik) => (
            <Form
              autoComplete="off"
              className="flex flex-column h-full w-full overflow-hidden"
            >
              <div className="formgrid grid mb-2 overflow-auto h-full">
                <div className="field col-12 md:col-12">
                  <FormikCalendar
                    key={`calendar-${formik.values.isScheduled}`}
                    label="Data jazdy"
                    name="date"
                    validationSchema={validationSchema}
                    isIndependent
                    locale="pl"
                    dateFormat="DD dd MM yy"
                    showTime={true}
                    disabled={formik.values?.isScheduled}
                  />
                </div>
                <div className="field col-12 md:col-12">
                  <div className="flex">
                    <div className="w-full p-0 m-0">
                      <FormikDropDown
                        label="Jeździec"
                        name="riderId"
                        validationSchema={validationSchema}
                        isIndependent
                        options={riderOptions}
                        disabled={!isAdmin}
                        afterChange={(e) =>
                          handleRiderChange(e, formik.values.horseId, formik)
                        }
                        filter
                      />
                    </div>
                    {formik.values.riderId !== undefined &&
                      (formik.values.id !== 0 ||
                        formik.values.scheduledRideId !== 0) && (
                        <div className="w-2 align-self-end text-right">
                          <Button
                            className="settings-button"
                            type="button"
                            icon="pi pi-arrow-right"
                            onClick={() =>
                              handleNavigateToRider(formik.values.riderId)
                            }
                          />
                        </div>
                      )}
                  </div>
                </div>

                <div className="field col-12 md:col-12">
                  <div className="flex">
                    <div className="w-full p-0 m-0">
                      <FormikDropDown
                        label="Koń"
                        name="horseId"
                        validationSchema={validationSchema}
                        isIndependent
                        options={horseOptions}
                        disabled={!isAdmin}
                        afterChange={(e) =>
                          handleHorseChange(formik.values.riderId, e, formik)
                        }
                        filter
                      />
                    </div>
                    {formik.values.horseId !== undefined &&
                      (formik.values.id !== 0 ||
                        formik.values.scheduledRideId !== 0) && (
                        <div className="w-2 align-self-end text-right">
                          <Button
                            className="settings-button"
                            type="button"
                            icon="pi pi-arrow-right"
                            onClick={() =>
                              handleNavigateToHorse(formik.values.horseId)
                            }
                          />
                        </div>
                      )}
                  </div>
                </div>

                <div className="field col-12 md:col-12">
                  <div className="flex">
                    <div className="col-12 p-0 m-0">
                      <FormikDropDown
                        label="Rodzaj jazdy"
                        name="rideTypeId"
                        validationSchema={validationSchema}
                        isIndependent
                        options={rideTypeOptions}
                        disabled={!hasWriteAccess}
                      />
                    </div>
                  </div>
                </div>

                <div className="field col-12 md:col-12">
                  <FormikDropDown
                    label="Czas trwania"
                    name="duration"
                    validationSchema={validationSchema}
                    isIndependent
                    options={durationOptions}
                    disabled={!hasWriteAccess}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <div className="flex">
                    <div className="w-full p-0 m-0">
                      <FormikDropDown
                        label="Instruktor"
                        name="userId"
                        validationSchema={validationSchema}
                        isIndependent
                        options={userOptions}
                        disabled={!hasWriteAccess}
                        filter
                      />
                    </div>
                    {formik.values.userId !== undefined &&
                      (formik.values.id !== 0 ||
                        formik.values.scheduledRideId !== 0) && (
                        <div className="w-2 align-self-end text-right">
                          <Button
                            className="settings-button"
                            type="button"
                            icon="pi pi-arrow-right"
                            onClick={() =>
                              handleNavigateToUser(formik.values.userId)
                            }
                          />
                        </div>
                      )}
                  </div>
                </div>
                <div className="field col-12 md:col-12">
                  <div className="flex">
                    <div className="col-12 p-0 m-0">
                      <FormikDropDown
                        label="Poziom miejętności"
                        name="skillLevelId"
                        validationSchema={validationSchema}
                        isIndependent
                        options={skillLevelOptions}
                        disabled={!hasWriteAccess}
                        filter
                      />
                    </div>
                  </div>
                </div>
                <div className="field col-8">
                  <FormikInputTextarea
                    label="Komentarz"
                    name="comment"
                    validationSchema={validationSchema}
                    isIndependent
                    disabled={!hasWriteAccess}
                  />
                </div>
                <div className="field col-4">
                  <div className="flex flex-column">
                    <FormikInputSwitch
                      checked={false}
                      label="Problem"
                      name="hasIssue"
                      validationSchema={validationSchema}
                      disabled={!hasWriteAccess}
                    />
                    <FormikInputSwitch
                      checked={false}
                      label="Upadek"
                      name="hasFall"
                      validationSchema={validationSchema}
                      disabled={!hasWriteAccess}
                    />
                  </div>
                </div>
                <div className="field col-12 md:col-12">
                  <div className="grid w-full align-items-center">
                    <div className="col-6">
                      <FormikInputSwitch
                        checked={false}
                        label="Powtarzaj"
                        name="isScheduled"
                        validationSchema={validationSchema}
                        disabled={!isAdmin}
                      />
                    </div>
                    {formik.values.isScheduled && (
                      <div className="col-6">
                        <FormikDropDown
                          label="Co ile tygodni"
                          name="weeksRepetition"
                          validationSchema={validationSchema}
                          isIndependent
                          options={[...Array(10)].map((_, i) => ({
                            label: `${i + 1} tyg.`,
                            value: i + 1,
                          }))}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {ride?.scheduledRideId &&
                  formik.values.isScheduled &&
                  isAdmin && (
                    <div className="field col-12 md:col-12">
                      <Button
                        icon="pi pi-trash"
                        type="button"
                        label="Usuń wszystko"
                        severity="danger"
                        className="mr-1 w-full"
                        onClick={onDeleteWithSchedule}
                      />
                    </div>
                  )}
                {(ride.id > 0 || ride.scheduledRideId) &&
                  !ride.isPaid &&
                  formik.values.rider?.useCredits && (
                    <div className="field col-12 md:col-6 flex justify-content-between">
                      <span className="align-self-center text-xl">
                        {`Pozostałe kredyty: ${formik.values.rider?.credits}`}
                      </span>
                      {hasWriteAccess && (
                        <Button
                          label="Opłać"
                          onClick={() => {
                            formik.setFieldValue("isPaid", true);
                            formik.submitForm();
                          }}
                        />
                      )}
                    </div>
                  )}
                {ride.id > 0 && ride.isPaid && (
                  <div className="field col-12 md:col-6 flex justify-content-between">
                    <span className="align-self-center text-xl">
                      {`Pozostałe kredyty: ${formik.values.rider?.credits}`}
                    </span>
                    <span className="align-self-center text-xl text-teal-700">
                      {"Jazda opłacona"}
                    </span>
                  </div>
                )}
              </div>

              <EditContentHeader
                header={ride?.id ? ride.date.toString() : "Dodaj jazde"}
                showCancelButton={false}
                showDeleteButton={
                  isAdmin && (!!ride.id || !!ride.scheduledRideId)
                }
                saveButtonDisabled={
                  !formik.dirty || !formik.isValid || !hasWriteAccess
                }
                onSaveClick={async () => {
                  if (!formik.isValid) {
                    toast.current?.show({
                      severity: "error",
                      detail: "Form invalid",
                    });
                    return;
                  }

                  // If creating a non-scheduled ride, ask about SMS before submit
                  const isNewSingleRide =
                    !formik.values.isScheduled &&
                    !formik.values.id &&
                    !formik.values.scheduledRideId;
                  if (isNewSingleRide) {
                    // Prefill phone from current rider selection (ride.rider or ridersQuery)
                    const selectedRiderId =
                      (formik.values.rider as any)?.id ?? formik.values.riderId;
                    let phone = (formik.values.rider as any)?.phoneNumber as
                      | string
                      | undefined;
                    if (!phone && selectedRiderId && ridersQuery.data) {
                      const r = ridersQuery.data.find(
                        (r) => r.id === selectedRiderId
                      );
                      phone = r?.phoneNumber;
                    }
                    setSmsPhone(phone ?? "");
                    setSmsSend(false);
                    setSmsDialogOpen(true);
                    return;
                  }

                  return formik.submitForm();
                }}
                onCancelClick={onCancel}
                onDeleteClick={onDelete}
              />

              {smsDialogOpen && (
                <CustomModal
                  isOpen={smsDialogOpen}
                  header="Wyślij SMS do jeźdźca?"
                  centered
                  justified
                  height="280px"
                  confirmation
                  onClose={() => setSmsDialogOpen(false)}
                  onConfirm={() => {
                    const opts: RideCreationSmsOptions = {
                      shouldSend: !!smsSend,
                      phoneNumber: smsPhone?.trim() || undefined,
                    };
                    setSmsOptions(opts);
                    setSmsDialogOpen(false);
                    formik.submitForm();
                  }}
                  confirmationButtonText="Zapisz"
                  body={
                    <div className="flex flex-column gap-3">
                      <div className="flex align-items-center gap-2">
                        <span>Wyślij SMS?</span>
                        <InputSwitch
                          checked={smsSend}
                          onChange={(e) => setSmsSend(!!e.value)}
                        />
                      </div>
                      <div className="flex flex-column gap-1">
                        <label>Numer telefonu (jeździec)</label>
                        <InputText
                          value={smsPhone}
                          placeholder="np. +48 600 700 800"
                          className={classNames({
                            "p-invalid":
                              !!smsPhone && !phoneRegExp.test(smsPhone),
                          })}
                          onChange={(e) => setSmsPhone(e.target.value)}
                          disabled={!smsSend}
                        />
                        {smsSend &&
                          !!smsPhone &&
                          !phoneRegExp.test(smsPhone) && (
                            <small className="p-error">
                              Nieprawidłowy numer telefonu
                            </small>
                          )}
                      </div>
                    </div>
                  }
                />
              )}
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}
